"use client";
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import * as XLSX from "xlsx";
import * as pdfjsLib from "pdfjs-dist";
import Tesseract from "tesseract.js";
import StatementScanner from "./StatementScanner";
import AlertDialog from "./AlertDialog";
import { FaTrash } from "react-icons/fa";
import BillMOReviewPanel from "./BillMOReviewPanel";
import { extractInvoiceDataAI, matchVendorFromText } from "./invoiceReader3";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.js";

const API = "https://erp-production-72da01c8e651.herokuapp.com/api";
const PAGE_SIZE = 50;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const cls = (...c) => c.filter(Boolean).join(" ");

// ── PDF/OCR text extraction — mirrors InvoiceUploadView3 ────────────────────
async function extractPdfText(file) {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  let text = "";

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();

    const Y_TOL = 3;
    const rows = [];
    for (const item of content.items) {
      if (!item.str || !item.str.trim()) continue;
      const x = item.transform[4];
      const y = item.transform[5];
      let row = rows.find((r) => Math.abs(r.y - y) <= Y_TOL);
      if (!row) {
        row = { y, items: [] };
        rows.push(row);
      }
      row.items.push({ x, str: item.str });
    }
    rows.sort((a, b) => b.y - a.y);
    for (const row of rows) {
      row.items.sort((a, b) => a.x - b.x);
      text += row.items.map((it) => it.str).join(" ") + "\n";
    }
    text += "\n";
  }
  return text;
}

async function rasterizeFirstPage(file) {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const page = await pdf.getPage(1);
  const vp = page.getViewport({ scale: 1.5 });
  const canvas = document.createElement("canvas");
  canvas.width = vp.width;
  canvas.height = vp.height;
  await page.render({ canvasContext: canvas.getContext("2d"), viewport: vp })
    .promise;
  return canvas.toDataURL("image/jpeg", 0.8);
}

async function extractTextFromFile(file) {
  const isPDF = file.type === "application/pdf";
  if (isPDF) {
    let text = await extractPdfText(file);
    if (text.trim().length < 50) {
      const preview = await rasterizeFirstPage(file);
      const { data } = await Tesseract.recognize(preview, "eng");
      text = data.text;
    }
    return text;
  }
  const url = URL.createObjectURL(file);
  const { data } = await Tesseract.recognize(url, "eng");
  URL.revokeObjectURL(url);
  return data.text;
}

// ─── Select styles ─────────────────────────────────────────────────────────────
const selectStyles = {
  control: (p, s) => ({
    ...p,
    backgroundColor: "#111827",
    borderColor: s.isFocused ? "#f97316" : "#374151",
    color: "#e5e7eb",
    boxShadow: s.isFocused ? "0 0 0 2px rgba(249,115,22,0.2)" : "none",
    "&:hover": { borderColor: "#f97316" },
    minHeight: "34px",
    fontSize: "13px",
    cursor: "pointer",
    borderRadius: "0.375rem",
  }),
  input: (p) => ({ ...p, color: "#e5e7eb", margin: 0, padding: 0 }),
  singleValue: (p) => ({ ...p, color: "#f3f4f6", fontSize: "13px" }),
  placeholder: (p) => ({ ...p, color: "#6b7280", fontSize: "13px" }),
  option: (p, s) => ({
    ...p,
    backgroundColor: s.isSelected
      ? "#92400e"
      : s.isFocused
        ? "#1f2937"
        : "#111827",
    color: s.isSelected ? "#fde68a" : "#e5e7eb",
    fontSize: "13px",
    cursor: "pointer",
    padding: "7px 12px",
    "&:active": { backgroundColor: "#78350f" },
  }),

  menu: (p) => ({
    ...p,
    backgroundColor: "#111827",
    borderRadius: "0.5rem",
    border: "1px solid #1f2937",
    boxShadow: "0 12px 28px rgba(0,0,0,0.5)",
    zIndex: 9999,
  }),
  menuList: (p) => ({ ...p, padding: "4px", maxHeight: "220px" }),
  valueContainer: (p) => ({ ...p, padding: "0 8px" }),
  indicatorsContainer: (p) => ({ ...p, padding: "0 4px" }),
  dropdownIndicator: (p, s) => ({
    ...p,
    padding: "4px",
    color: s.isFocused ? "#f97316" : "#4b5563",
    "&:hover": { color: "#f97316" },
  }),
  clearIndicator: (p) => ({
    ...p,
    padding: "4px",
    color: "#4b5563",
    "&:hover": { color: "#f87171" },
  }),
  indicatorSeparator: (p) => ({ ...p, backgroundColor: "#1f2937" }),

  noOptionsMessage: (p) => ({
    ...p,
    color: "#6b7280",
    fontSize: "13px",
    padding: "10px 12px",
  }),
  menuPortal: (p) => ({ ...p, zIndex: 9999 }),
};

// ─── Status badges ────────────────────────────────────────────────────────────
const InvoiceStatusBadge = ({ status }) => {
  const map = {
    paid: "bg-emerald-900/60 text-emerald-300 border-emerald-700",
    unpaid: "bg-red-900/60 text-red-300 border-red-700",
    partial: "bg-amber-900/60 text-amber-300 border-amber-700",
  };
  return (
    <span
      className={cls(
        "inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border",
        map[status] ?? "bg-gray-700 text-gray-300 border-gray-600",
      )}
    >
      {status ? status.charAt(0).toUpperCase() + status.slice(1) : "—"}
    </span>
  );
};

const MoStatusBadge = ({ status }) => {
  if (!status) return <span className="text-gray-600 text-xs">—</span>;
  const map = {
    delivered: "text-emerald-400",
    partial: "text-amber-400",
    confirmed: "text-blue-400",
    pending: "text-gray-400",
    void: "text-red-400",
  };
  return (
    <span
      className={cls("text-xs font-medium", map[status] ?? "text-gray-400")}
    >
      {status.toUpperCase()}
    </span>
  );
};

// ─── Column header ────────────────────────────────────────────────────────────
const Th = ({ children, active, onClick, onMouseDown, className = "" }) => (
  <th
    onClick={onClick}
    onMouseDown={onMouseDown}
    className={cls(
      "px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wider select-none whitespace-nowrap",
      onClick || onMouseDown
        ? "cursor-pointer hover:text-orange-300 transition-colors"
        : "",
      active ? "text-orange-400" : "text-gray-400",
      className,
    )}
  >
    {children}
  </th>
);

// ─── Bill file upload row ─────────────────────────────────────────────────
// First file on an empty bill: auto-extracts and fills the form directly
// (existing behavior). Any ADDITIONAL file — e.g. the vendor's second
// invoice covering the balance of the same order — is also OCR'd, but
// instead of silently overwriting anything, a confirm banner appears:
// "This file looks like invoice X ($Y) — add to this bill?" The user
// clicks Add to fold its invoice # + subtotal/tax/total into the existing
// form (same summing logic as the upload page's merge), or Skip to leave
// it as a plain attachment with no financial impact.
const BillFileSection = ({
  files,
  setFiles,
  existingAtts = [],
  setSelectedBill,
  setForm, // (updater) => void — used to add a subsequent file's data into the form
  defaultTaxRate = 0,
  labelCls,
  onExtracted, // (extractedFields) => void — used only for the FIRST file
  vendors = [], // full vendor objects, for name matching
  alreadyHasInvoiceNumber = false, // skip first-file auto-read once bill is identified
}) => {
  const [extracting, setExtracting] = useState(false);
  const [extractError, setExtractError] = useState(null);
  // Pending "add this additional invoice?" suggestion awaiting user confirm
  const [pendingAdd, setPendingAdd] = useState(null); // { file, extracted } | null

  const runExtraction = async (file) => {
    const text = await extractTextFromFile(file);
    if (!text.trim()) throw new Error("Could not read text from file");
    const extracted = await extractInvoiceDataAI(text);
    if (extracted?.error) throw new Error("AI extraction failed");
    const vendorId = matchVendorFromText(text, extracted.vendorName, vendors);
    return { ...extracted, vendorId };
  };

  const handleChange = async (e) => {
    const newFiles = Array.from(e.target.files);
    if (!newFiles.length) return;

    const isFirstFileOverall = files.length === 0 && existingAtts.length === 0;
    setFiles((prev) => [...prev, ...newFiles]);
    e.target.value = null; // allow re-selecting the same filename later

    const file = newFiles[0];

    // First file on an empty bill: extract and fill the form directly,
    // same as today.
    if (isFirstFileOverall && !alreadyHasInvoiceNumber && onExtracted) {
      setExtracting(true);
      setExtractError(null);
      try {
        const extracted = await runExtraction(file);
        onExtracted(extracted);
      } catch (err) {
        setExtractError(
          err.message === "Could not read text from file"
            ? "Could not read text — fill in fields manually."
            : `Read failed: ${err.message}`,
        );
      } finally {
        setExtracting(false);
      }
      return;
    }

    // Any additional file: still OCR it, but surface as a confirm-first
    // suggestion rather than silently changing the bill's totals.
    if (!setForm) return; // caller didn't wire up merge support — skip
    setExtracting(true);
    setExtractError(null);
    try {
      const extracted = await runExtraction(file);
      // Only worth suggesting if it actually found something useful
      if (extracted.invoiceNumber || extracted.subtotal != null) {
        setPendingAdd({ file, extracted });
      }
    } catch {
      // Silent for additional files — it's just a plain attachment then
    } finally {
      setExtracting(false);
    }
  };

  const confirmAddPending = () => {
    if (!pendingAdd || !setForm) return;
    const { extracted } = pendingAdd;

    setForm((p) => {
      const existingNums = p.invoiceNumbers?.length
        ? p.invoiceNumbers
        : p.invoiceNumber
          ? p.invoiceNumber.split(" ")
          : [];
      const nextNums = extracted.invoiceNumber
        ? [...existingNums, extracted.invoiceNumber]
        : existingNums;

      const prevSub = parseFloat(p.subtotal) || 0;
      const addSub = extracted.subtotal != null ? extracted.subtotal : 0;
      const nextSub = +(prevSub + addSub).toFixed(2);

      const prevTax = parseFloat(p.tax) || 0;
      const addTax =
        extracted.hst != null
          ? extracted.hst
          : +(addSub * defaultTaxRate).toFixed(2);
      const nextTax = +(prevTax + addTax).toFixed(2);

      const prevTotal = parseFloat(p.total) || 0;
      const addTotal =
        extracted.total != null
          ? extracted.total
          : +(addSub + addTax).toFixed(2);
      const nextTotal = +(prevTotal + addTotal).toFixed(2);

      return {
        ...p,
        invoiceNumbers: nextNums,
        subtotal: nextSub,
        tax: nextTax,
        total: nextTotal,
      };
    });
    setPendingAdd(null);
  };

  const skipPending = () => setPendingAdd(null);

  const removeExisting = (url) => {
    if (!setSelectedBill) return;
    setSelectedBill((p) => ({
      ...p,
      attachments: p.attachments.filter((a) => a.url !== url),
    }));
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <label className={labelCls}>Bill / Invoice Files</label>
        {extracting && (
          <span className="text-[10px] text-teal-400 flex items-center gap-1">
            <svg
              className="w-3 h-3 animate-spin"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8H4z"
              />
            </svg>
            Reading invoice…
          </span>
        )}
      </div>

      {extractError && (
        <p className="text-[10px] text-amber-400 mb-1">⚠ {extractError}</p>
      )}

      {pendingAdd && (
        <div className="mb-2 rounded-lg border border-indigo-700/50 bg-indigo-950/20 px-3 py-2 flex items-start gap-2">
          <span className="text-sm leading-none mt-0.5">🔗</span>
          <div className="flex-1 min-w-0 text-xs text-indigo-200">
            <p>
              This file looks like invoice{" "}
              <span className="font-mono font-semibold">
                {pendingAdd.extracted.invoiceNumber || pendingAdd.file.name}
              </span>
              {pendingAdd.extracted.subtotal != null && (
                <>
                  {" "}
                  (subtotal{" "}
                  <span className="font-mono font-semibold">
                    ${pendingAdd.extracted.subtotal.toFixed(2)}
                  </span>
                  )
                </>
              )}
              . Add its invoice # and amount to this bill?
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={confirmAddPending}
              className="px-2 py-1 text-[11px] font-semibold rounded bg-indigo-600 hover:bg-indigo-500 text-white transition-colors"
            >
              Add
            </button>
            <button
              onClick={skipPending}
              className="px-2 py-1 text-[11px] text-indigo-400 hover:text-indigo-200 transition-colors"
            >
              Skip
            </button>
          </div>
        </div>
      )}

      {existingAtts.length > 0 && (
        <div className="mb-2 space-y-1">
          {existingAtts.map((a, i) => (
            <div key={i} className="flex items-center gap-2">
              <a
                href={a.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-400 hover:text-blue-300 hover:underline truncate max-w-xs"
              >
                {a.name}
              </a>
              <button
                onClick={() => removeExisting(a.url)}
                className="text-red-500 hover:text-red-400 text-xs flex-shrink-0"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
      {files.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1">
          {files.map((f, i) => (
            <div
              key={i}
              className="flex items-center gap-1 px-2 py-0.5 rounded bg-gray-700 text-gray-300 text-[10px]"
            >
              {f.name}
              <button
                onClick={() => setFiles((p) => p.filter((_, j) => j !== i))}
                className="text-red-500 hover:text-red-400 ml-1"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
      <input
        type="file"
        multiple
        accept="application/pdf,image/*"
        disabled={extracting}
        onChange={handleChange}
        className="block w-full text-xs text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-gray-800 file:text-gray-300 hover:file:bg-gray-700 disabled:opacity-50 file:transition-colors"
      />
      <p className="text-[10px] text-gray-600 mt-1">
        ✦ First file uploaded is auto-read to fill invoice #, dates, and totals
      </p>
    </div>
  );
};

// ─── Credit memo section — AI-reads PDF on file select ─────────────────────
const CreditSection = ({ credits, setForm, labelCls, inputCls }) => {
  const [extractingIdx, setExtractingIdx] = useState(null);

  const updateCredit = (i, updates) =>
    setForm((p) => ({
      ...p,
      credits: (p.credits || []).map((c, idx) =>
        idx === i ? { ...c, ...updates } : c,
      ),
    }));

  const addCredit = () =>
    setForm((p) => ({
      ...p,
      credits: [
        ...(p.credits || []),
        { description: "", amount: "", attachment: null, _newFile: null },
      ],
    }));

  const removeCredit = (i) =>
    setForm((p) => ({
      ...p,
      credits: (p.credits || []).filter((_, idx) => idx !== i),
    }));

  const handleCreditFile = async (i, file) => {
    if (!file) return;
    updateCredit(i, { _newFile: file });

    if (file.type !== "application/pdf") return; // images: manual entry only

    setExtractingIdx(i);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/bills3/read-credit`, fd, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const { extracted = {} } = res.data;
      updateCredit(i, {
        amount: extracted.creditAmount ?? "",
        description: extracted.creditNumber
          ? `Credit Memo #${extracted.creditNumber}`
          : "Credit Memo",
      });
    } catch {
      // Silent — user can fill manually
    } finally {
      setExtractingIdx(null);
    }
  };

  const totalCredits = (credits || []).reduce(
    (s, c) => s + (parseFloat(c.amount) || 0),
    0,
  );

  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs text-gray-500 leading-relaxed">
          Upload vendor credit memos here. The credit amount is automatically
          applied as a negative surcharge on the linked Material Order so both
          sides stay in sync — no manual MO edit needed.
        </p>
        <button
          type="button"
          onClick={addCredit}
          className="flex-shrink-0 px-2 py-0.5 text-xs rounded bg-gray-700 border border-gray-600 text-gray-300 hover:bg-gray-600 transition-colors"
        >
          + Add Credit
        </button>
      </div>

      {(credits || []).length === 0 && (
        <p className="text-xs text-gray-600 italic">No credit memos applied.</p>
      )}

      {(credits || []).map((credit, i) => (
        <div
          key={i}
          className="p-3 rounded-lg border border-amber-800/40 bg-amber-950/20 space-y-2"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-amber-400">
              Credit #{i + 1}
              {extractingIdx === i && (
                <span className="ml-2 text-gray-500 font-normal animate-pulse">
                  Reading PDF…
                </span>
              )}
            </span>
            <button
              type="button"
              onClick={() => removeCredit(i)}
              className="text-red-500 hover:text-red-400 text-xs"
            >
              ✕ Remove
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelCls}>Description</label>
              <input
                className={inputCls}
                value={credit.description}
                onChange={(e) =>
                  updateCredit(i, { description: e.target.value })
                }
                placeholder="e.g. Credit Memo #CM-001"
              />
            </div>
            <div>
              <label className={labelCls}>
                Credit Amount ($){" "}
                <span className="text-gray-600 font-normal normal-case">
                  (enter positive)
                </span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                className={inputCls}
                value={credit.amount}
                onChange={(e) => updateCredit(i, { amount: e.target.value })}
                placeholder="0.00"
              />
            </div>
          </div>

          {/* Existing Cloudinary attachment */}
          {credit.attachment?.url && (
            <a
              href={credit.attachment.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300 hover:underline"
            >
              📄 {credit.attachment.name || "Credit file"}
            </a>
          )}

          {/* File picker — AI reads PDF immediately */}
          <div>
            <label className={labelCls}>
              Upload Credit Invoice{" "}
              <span className="text-teal-400 font-normal normal-case">
                ✦ AI auto-reads PDF
              </span>
            </label>
            {credit._newFile && (
              <div className="flex items-center gap-1 mb-1 px-2 py-0.5 rounded bg-gray-700 text-gray-300 text-[10px] w-fit">
                {credit._newFile.name}
                <button
                  onClick={() => updateCredit(i, { _newFile: null })}
                  className="text-red-500 hover:text-red-400 ml-1"
                >
                  ✕
                </button>
              </div>
            )}
            <input
              type="file"
              accept="application/pdf,image/*"
              disabled={extractingIdx === i}
              onChange={(e) => handleCreditFile(i, e.target.files[0] || null)}
              className="block w-full text-xs text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-gray-800 file:text-gray-300 hover:file:bg-gray-700 disabled:opacity-50 file:transition-colors"
            />
          </div>
        </div>
      ))}

      {/* Summary row */}
      {totalCredits > 0 && (
        <div className="pt-2 border-t border-amber-800/30 text-xs text-right space-y-0.5">
          <div className="text-amber-400">
            Total credits applied:{" "}
            <span className="font-mono font-semibold">
              −${fmt(totalCredits)}
            </span>
          </div>
          <p className="text-gray-600">
            A negative surcharge of this amount will be saved to the linked MO.
          </p>
        </div>
      )}
    </div>
  );
};

// ─── Invoice numbers list — repeatable "invoice #" entries ──────────────────
// Joined with spaces into billName/invoiceNumber on save, matching the
// existing schema (e.g. "IN1-0024392 IN1-0024393").
const InvoiceNumbersField = ({
  invoiceNumbers,
  setForm,
  labelCls,
  inputCls,
}) => {
  const list = invoiceNumbers.length ? invoiceNumbers : [""];

  const updateAt = (i, value) =>
    setForm((p) => {
      const next = [...(p.invoiceNumbers?.length ? p.invoiceNumbers : [""])];
      next[i] = value;
      return { ...p, invoiceNumbers: next };
    });

  const addOne = () =>
    setForm((p) => ({
      ...p,
      invoiceNumbers: [
        ...(p.invoiceNumbers?.length ? p.invoiceNumbers : [""]),
        "",
      ],
    }));

  const removeAt = (i) =>
    setForm((p) => {
      const next = (p.invoiceNumbers?.length ? p.invoiceNumbers : [""]).filter(
        (_, idx) => idx !== i,
      );
      return { ...p, invoiceNumbers: next.length ? next : [""] };
    });

  return (
    <div>
      <div className="flex items-center justify-between">
        <label className={labelCls}>
          Invoice #{list.length > 1 ? "s" : ""}{" "}
          <span className="text-red-400">*</span>
        </label>
        <button
          type="button"
          onClick={addOne}
          className="px-2 py-0.5 text-[11px] rounded bg-gray-700 border border-gray-600 text-gray-300 hover:bg-gray-600 transition-colors"
        >
          + Add invoice #
        </button>
      </div>
      <div className="space-y-1.5">
        {list.map((val, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              className={inputCls}
              value={val}
              onChange={(e) => updateAt(i, e.target.value)}
              placeholder={i === 0 ? "e.g. IN1-0024392" : "e.g. IN1-0024393"}
            />
            {list.length > 1 && (
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="text-red-500 hover:text-red-400 text-xs flex-shrink-0"
                title="Remove"
              >
                ✕
              </button>
            )}
          </div>
        ))}
      </div>
      {list.filter((v) => v.trim()).length > 1 && (
        <p className="text-[10px] text-gray-600 mt-1">
          Saved as:{" "}
          <span className="font-mono text-gray-500">
            {list.filter((v) => v.trim()).join(" ")}
          </span>
        </p>
      )}
    </div>
  );
};

// ─── Shared bill form — module-level so inputs keep focus ───────────────────
const BillForm = ({
  form,
  setForm,
  isCreate = false,
  // option builders + data
  projectOptions,
  vendorOptions,
  subOptions,
  vendors = [],
  v3Projects,
  getPOOptions,
  getWOOptions,
  getMOOptions,
  loadMOs,
  // styling
  selectStyles,
  inputCls,
  labelCls,
  // files
  createBillFiles,
  setCreateBillFiles,
  newBillFiles,
  setNewBillFiles,
  setSelectedBill,
  // tax
  defaultTaxRate,
}) => {
  const projectId = form.projectId || form._projectId;
  const isWO = form.isWO ?? form._isWO ?? false;

  const poOpts = getPOOptions(projectId);
  const woOpts = getWOOptions(projectId);
  const moOpts = getMOOptions(projectId);

  const setField = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  // ── Apply AI-extracted invoice data — never overwrites fields already
  // filled in by the user ──────────────────────────────────────────────────
  const handleExtracted = async (extracted) => {
    setForm((p) => {
      const next = { ...p };

      const hasInvoiceNum = p.invoiceNumbers?.some((v) => v.trim());
      if (!hasInvoiceNum && extracted.invoiceNumber) {
        next.invoiceNumbers = [extracted.invoiceNumber];
      }
      if (!p.billName && extracted.invoiceNumber) {
        next.billName = extracted.invoiceNumber;
      }
      if (!p.invoiceDate && extracted.invoiceDate) {
        next.invoiceDate = extracted.invoiceDate;
      }
      if (!p.dueDate && extracted.dueDate) {
        next.dueDate = extracted.dueDate;
      }
      if (
        (p.subtotal === "" || p.subtotal == null) &&
        extracted.subtotal != null
      ) {
        next.subtotal = extracted.subtotal;
        const tax =
          extracted.hst != null
            ? extracted.hst
            : +(extracted.subtotal * defaultTaxRate).toFixed(2);
        next.tax = tax;
        next.total =
          extracted.total != null
            ? extracted.total
            : +(extracted.subtotal + tax).toFixed(2);
      }
      if (!p.payToId && extracted.vendorId) {
        next.payToId = extracted.vendorId;
      }

      return next;
    });

    // PO#/MO# lookup — auto-fills project + MO + PO + vendor when the
    // vendor's PO number follows the "PROJ#-MO#" convention.
    if (extracted.poNumber) {
      try {
        const lookupRes = await axios.get(
          `${API}/projects/lookupV3/${encodeURIComponent(extracted.poNumber)}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
        const { project: proj, mo, po, vendor: v } = lookupRes.data;
        const projId = String(proj._id);

        setForm((p) => ({
          ...p,
          projectId: p.projectId || projId,
          _projectId: p._projectId || projId,
          isWO: p.isWO ?? false,
          _isWO: p._isWO ?? false,
          relatedMO3Id: p.relatedMO3Id || String(mo._id),
          relatedPO3Id: p.relatedPO3Id || (po ? String(po._id) : ""),
          payToId: p.payToId || (v ? String(v._id) : p.payToId),
        }));
        loadMOs(projId);
      } catch {
        // Silent — user can select project/MO manually
      }
    }
  };

  // ── Subtotal drives tax + total ──
  const handleSubtotalChange = (value) => {
    const sub = parseFloat(value);
    if (isNaN(sub)) {
      // keep raw string so the user can type "12." etc.
      setForm((p) => ({ ...p, subtotal: value }));
      return;
    }
    const tax = +(sub * defaultTaxRate).toFixed(2);
    const total = +(sub + tax).toFixed(2);
    setForm((p) => ({ ...p, subtotal: value, tax, total }));
  };

  // ── Manual tax override still recomputes total ──
  const handleTaxChange = (value) => {
    const tax = parseFloat(value);
    const sub = parseFloat(form.subtotal) || 0;
    if (isNaN(tax)) {
      setForm((p) => ({ ...p, tax: value }));
      return;
    }
    const total = +(sub + tax).toFixed(2);
    setForm((p) => ({ ...p, tax: value, total }));
  };

  const handleFeePercentChange = (value) => {
    const pct = parseFloat(value);
    const credits = (form.credits || []).reduce(
      (s, c) => s + (parseFloat(c.amount) || 0),
      0,
    );
    const base = Math.max(0, (parseFloat(form.total) || 0) - credits);
    setForm((p) => ({
      ...p,
      paymentFeePercent: value,
      paymentFeeAmount: isNaN(pct) ? 0 : +((base * pct) / 100).toFixed(2),
    }));
  };

  const handleProjectChange = (sel) => {
    setForm((p) => ({
      ...p,
      projectId: sel?.value || "",
      _projectId: sel?.value || "",
      relatedPO3Id: "",
      relatedMO3Id: "",
      relatedWO3Id: "",
      payToId: "",
      subContractorId: "",
    }));
    if (sel?.value) loadMOs(sel.value);
  };

  const handleMOChange = (sel) => {
    setField("relatedMO3Id", sel?.value || "");
    if (sel?.poId) setField("relatedPO3Id", sel.poId);
  };

  return (
    <div className="p-6 space-y-6">
      {/* ── Bill Details ── */}
      <div>
        <h4 className={labelCls}>Bill Details</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
          <div>
            <label className={labelCls}>
              Bill Name{" "}
              <span className="text-gray-600 font-normal normal-case">
                (auto-filled from invoice #s, editable)
              </span>
            </label>
            <input
              className={inputCls}
              value={form.billName || ""}
              onChange={(e) => setField("billName", e.target.value)}
              placeholder="e.g. Vendor Invoice Jan 2025"
            />
          </div>

          <div>
            <InvoiceNumbersField
              invoiceNumbers={
                form.invoiceNumbers ||
                (form.invoiceNumber ? form.invoiceNumber.split(" ") : [""])
              }
              setForm={setForm}
              labelCls={labelCls}
              inputCls={inputCls}
            />
          </div>

          <div>
            <label className={labelCls}>
              Invoice Date <span className="text-red-400">*</span>
            </label>
            <input
              type="date"
              className={inputCls}
              value={form.invoiceDate || ""}
              onChange={(e) => setField("invoiceDate", e.target.value)}
            />
          </div>
          <div>
            <label className={labelCls}>Due Date</label>
            <input
              type="date"
              className={inputCls}
              value={form.dueDate || ""}
              onChange={(e) => setField("dueDate", e.target.value)}
            />
          </div>
          <div>
            <label className={labelCls}>Date Paid</label>
            <input
              type="date"
              className={inputCls}
              value={form.datePaid || ""}
              onChange={(e) => setField("datePaid", e.target.value)}
            />
          </div>
          <div>
            <label className={labelCls}>Invoice Status</label>
            <select
              className={inputCls}
              value={form.invoiceStatus || "unpaid"}
              onChange={(e) => setField("invoiceStatus", e.target.value)}
            >
              <option value="unpaid">Unpaid</option>
              <option value="paid">Paid</option>
              <option value="partial">Partial</option>
            </select>
          </div>

          {isCreate ? (
            <div className="sm:col-span-2">
              <label className={labelCls}>
                Project (V3 only) <span className="text-red-400">*</span>
              </label>
              <Select
                options={projectOptions}
                value={
                  projectOptions.find(
                    (o) => o.value === (form.projectId || form._projectId),
                  ) || null
                }
                onChange={handleProjectChange}
                styles={selectStyles}
                placeholder={
                  projectOptions.length === 0
                    ? "No V3 projects found…"
                    : "Select V3 project…"
                }
                isClearable
              />
            </div>
          ) : (
            <div className="sm:col-span-2">
              <label className={labelCls}>Project</label>
              <p className="text-sm text-gray-300 py-2 px-3 bg-gray-800 rounded border border-gray-700">
                {form.project?.projectName ||
                  v3Projects.find(
                    (p) => String(p._id) === String(form._projectId),
                  )?.projectName ||
                  "—"}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── Order Type toggle ── */}
      <div>
        <h4 className={labelCls}>Order Type</h4>
        <div className="flex gap-3 mt-2">
          {[
            { label: "Purchase Order", value: false },
            { label: "Work Order", value: true },
          ].map((opt) => (
            <button
              key={String(opt.value)}
              type="button"
              onClick={() => {
                setField("isWO", opt.value);
                setField("_isWO", opt.value);
                if (opt.value) {
                  setField("relatedPO3Id", "");
                  setField("relatedMO3Id", "");
                  setField("payToId", "");
                } else {
                  setField("relatedWO3Id", "");
                  setField("subContractorId", "");
                }
              }}
              className={cls(
                "px-4 py-2 text-sm rounded-lg border font-medium transition-colors",
                isWO === opt.value
                  ? "bg-orange-600 border-orange-500 text-white"
                  : "bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-500",
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── PO / MO / Vendor  OR  WO / Sub ── */}
      <div>
        <h4 className={labelCls}>
          {isWO
            ? "Work Order & Subcontractor"
            : "Purchase Order, Material Order & Vendor"}
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
          {isWO ? (
            <>
              <div>
                <label className={labelCls}>Work Order (WO3)</label>
                <Select
                  options={woOpts}
                  value={
                    woOpts.find(
                      (o) =>
                        o.value === (form.relatedWO3Id || form.relatedWO3?._id),
                    ) || null
                  }
                  onChange={(sel) => setField("relatedWO3Id", sel?.value || "")}
                  styles={selectStyles}
                  placeholder={
                    !projectId
                      ? "Select project first"
                      : woOpts.length === 0
                        ? "No WOs found"
                        : "Select WO3…"
                  }
                  isDisabled={!projectId}
                  isClearable
                />
                {(form.relatedMO3Id || form.relatedMO3?._id) &&
                  (form.projectId || form._projectId) && (
                    <Link
                      to={
                        "/Projects/" +
                        (form.projectId || form._projectId) +
                        "/material-orders/" +
                        (form.relatedMO3Id || form.relatedMO3?._id)
                      }
                      target="_blank"
                      className="inline-block mt-1 text-[10px] text-orange-400 hover:text-orange-300 transition-colors"
                    >
                      ↗ Open MO in new tab
                    </Link>
                  )}
              </div>

              <div>
                <label className={labelCls}>Subcontractor</label>
                <Select
                  options={subOptions}
                  value={
                    subOptions.find(
                      (o) =>
                        o.value ===
                        (form.subContractorId || form.subContractor?._id),
                    ) || null
                  }
                  onChange={(sel) =>
                    setField("subContractorId", sel?.value || "")
                  }
                  styles={selectStyles}
                  placeholder="Select subcontractor…"
                  isClearable
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <label className={labelCls}>
                  Material Order (MO3)
                  {form.relatedPO3Id && (
                    <span className="text-red-400 ml-1">*</span>
                  )}
                </label>
                <Select
                  options={moOpts}
                  value={
                    moOpts.find(
                      (o) =>
                        o.value === (form.relatedMO3Id || form.relatedMO3?._id),
                    ) || null
                  }
                  onChange={handleMOChange}
                  styles={selectStyles}
                  placeholder={
                    !projectId
                      ? "Select project first"
                      : moOpts.length === 0
                        ? "No MOs found"
                        : "Select MO3…"
                  }
                  isDisabled={!projectId}
                  isClearable
                  formatOptionLabel={(opt) => (
                    <div className="flex items-center justify-between">
                      <span>{opt.label}</span>
                      {opt.status === "delivered" && (
                        <span className="text-[10px] text-emerald-400 font-semibold ml-2">
                          ✓ DELIVERED
                        </span>
                      )}
                    </div>
                  )}
                />
                {form.relatedMO3Id &&
                  (() => {
                    const mo = moOpts.find(
                      (o) => o.value === form.relatedMO3Id,
                    );
                    return mo && mo.status !== "delivered" ? (
                      <p className="text-xs text-amber-400 mt-1">
                        ⚠ MO must be delivered before confirming
                      </p>
                    ) : null;
                  })()}
              </div>

              <div>
                <label className={labelCls}>
                  Purchase Order (PO3){" "}
                  <span className="text-gray-600 font-normal normal-case">
                    (auto-filled by MO)
                  </span>
                </label>
                <Select
                  options={poOpts}
                  value={
                    poOpts.find(
                      (o) =>
                        o.value === (form.relatedPO3Id || form.relatedPO3?._id),
                    ) || null
                  }
                  onChange={(sel) => setField("relatedPO3Id", sel?.value || "")}
                  styles={selectStyles}
                  placeholder={
                    !projectId
                      ? "Select project first"
                      : poOpts.length === 0
                        ? "No POs found"
                        : "Select PO3…"
                  }
                  isDisabled={!projectId}
                  isClearable
                />
              </div>

              <div>
                <label className={labelCls}>Vendor</label>
                <Select
                  options={vendorOptions}
                  value={
                    vendorOptions.find(
                      (o) => o.value === (form.payToId || form.payTo?._id),
                    ) || null
                  }
                  onChange={(sel) => setField("payToId", sel?.value || "")}
                  styles={selectStyles}
                  placeholder="Select vendor…"
                  isClearable
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Financials: subtotal drives tax + total ── */}
      <div>
        <h4 className={labelCls}>Financials</h4>
        <div className="grid grid-cols-3 gap-4 mt-3">
          <div>
            <label className={labelCls}>Subtotal</label>
            <input
              type="number"
              step="0.01"
              className={inputCls}
              value={form.subtotal ?? ""}
              onChange={(e) => handleSubtotalChange(e.target.value)}
              placeholder="0.00"
            />
          </div>
          <div>
            <label className={labelCls}>
              Tax{" "}
              <span className="text-gray-600 font-normal normal-case">
                ({(defaultTaxRate * 100).toFixed(2)}%)
              </span>
            </label>
            <input
              type="number"
              step="0.01"
              className={inputCls}
              value={form.tax ?? ""}
              onChange={(e) => handleTaxChange(e.target.value)}
              placeholder="0.00"
            />
          </div>
          <div>
            <label className={labelCls}>Total</label>
            <input
              type="number"
              step="0.01"
              className={inputCls}
              value={form.total ?? ""}
              onChange={(e) => setField("total", e.target.value)}
              placeholder="0.00"
            />
          </div>
        </div>
      </div>

      {/* ── Payment processing fee (edit only) ── */}
      {!isCreate && (
        <div>
          <h4 className={labelCls}>Payment Processing Fee</h4>
          <div className="grid grid-cols-3 gap-4 mt-3">
            <div>
              <label className={labelCls}>Fee %</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className={inputCls}
                value={form.paymentFeePercent ?? ""}
                onChange={(e) => handleFeePercentChange(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className={labelCls}>Fee Amount ($)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className={inputCls}
                value={form.paymentFeeAmount ?? ""}
                onChange={(e) => setField("paymentFeeAmount", e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className={labelCls}>Note</label>
              <input
                className={inputCls}
                value={form.paymentFeeNote || ""}
                onChange={(e) => setField("paymentFeeNote", e.target.value)}
                placeholder="e.g. Visa surcharge"
              />
            </div>
          </div>
          <p className="text-[10px] text-gray-600 mt-1">
            Tracked as a cost only. It doesn't change the bill total or the MO
            match.
          </p>
        </div>
      )}

      {/* ── Invoice attachment ── */}
      <div>
        <h4 className={labelCls}>Invoice / Bill Attachment</h4>
        <div className="bg-blue-950/20 rounded-lg border border-blue-800/30 p-4 mt-2">
          <p className="text-xs text-gray-500 mb-3">
            Upload the vendor invoice or bill PDF here. Sales orders and packing
            slips are attached to the Material Order.
          </p>
          <BillFileSection
            files={isCreate ? createBillFiles : newBillFiles}
            setFiles={isCreate ? setCreateBillFiles : setNewBillFiles}
            existingAtts={form.attachments || []}
            setSelectedBill={setSelectedBill}
            setForm={setForm}
            defaultTaxRate={defaultTaxRate}
            labelCls={labelCls}
            onExtracted={handleExtracted}
            vendors={vendors}
            alreadyHasInvoiceNumber={
              !!(
                form.invoiceNumbers?.some((v) => v.trim()) || form.invoiceNumber
              )
            }
          />
          <p className="text-[10px] text-gray-600 mt-2">
            ✦ Additional files are read too — you'll get a prompt to add their
            invoice # and amount to this bill (e.g. the vendor's balance on
            another invoice for the same order).
          </p>
        </div>
      </div>

      {/* ── Credits / Credit Memos ── */}
      <div>
        <h4 className={labelCls}>Credits &amp; Adjustments</h4>
        <div className="bg-amber-950/20 rounded-lg border border-amber-800/30 p-4 mt-2">
          <CreditSection
            credits={form.credits || []}
            setForm={setForm}
            labelCls={labelCls}
            inputCls={inputCls}
          />
        </div>
      </div>

      {/* ── Notes ── */}
      <div>
        <label className={labelCls}>Notes</label>
        <textarea
          rows={2}
          className={inputCls + " resize-none"}
          value={form.notes || ""}
          onChange={(e) => setField("notes", e.target.value)}
          placeholder="Optional internal notes…"
        />
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
export default function Bills3TablePage() {
  const navigate = useNavigate();

  // ── Data state ──
  const [bills, setBills] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [defaultTaxRate, setDefaultTaxRate] = useState(0); // stored as a fraction, e.g. 0.13
  // ── Filter / sort state ──
  const [activeTab, setActiveTab] = useState("pending");
  const [sortField, setSortField] = useState("createdAt");
  const [bulkInvDate, setBulkInvDate] = useState("");
  const [bulkPaidDate, setBulkPaidDate] = useState(""); // ← new
  const [bulkDueDate, setBulkDueDate] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkFeePct, setBulkFeePct] = useState("");
  const [bulkPaidFeePct, setBulkPaidFeePct] = useState("");
  const moNumberRef = useRef(null);
  const [sortDir, setSortDir] = useState("desc");
  const [filterState, setFilterState] = useState({
    billName: "",
    invoiceNumber: "",
    vendor: "",
    project: "",
    invoiceStatus: "",
    moNumber: "", // ← new
  });
  const [showFilter, setShowFilter] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  // ── Reference data — V3 projects ONLY ──
  const [v3Projects, setV3Projects] = useState([]); // [{_id, projectName, purchaseOrders, workOrders, materialOrders}]
  const [vendors, setVendors] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [filterVendors, setFilterVendors] = useState([]);

  // ── Edit modal ──
  const [selectedBill, setSelectedBill] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [newBillFiles, setNewBillFiles] = useState([]);

  // ── Bulk mark-paid-by-invoice-list modal ──
  const [showBulkPaid, setShowBulkPaid] = useState(false);
  const [bulkPaidText, setBulkPaidText] = useState("");
  const [bulkPaidDate2, setBulkPaidDate2] = useState("");
  const [bulkPaidRunning, setBulkPaidRunning] = useState(false);
  const [bulkPaidPreviewing, setBulkPaidPreviewing] = useState(false);
  const [bulkPaidConfirming, setBulkPaidConfirming] = useState(false);
  const [bulkPaidResult, setBulkPaidResult] = useState(null); // preview result — always a dry run
  const [bulkPaidCommitted, setBulkPaidCommitted] = useState(null); // final result after confirm

  // ── Create modal ──
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(null);
  const [createSaving, setCreateSaving] = useState(false);
  const [createBillFiles, setCreateBillFiles] = useState([]);

  // ── POs / MOs / WOs lazy-loaded per project ──
  // optionsV2 already returns POs and WOs; MOs are loaded separately on demand.
  const [mosByProject, setMosByProject] = useState({});
  const [reviewBill, setReviewBill] = useState(null);
  // ── Selection ──
  const [selectedIds, setSelectedIds] = useState([]);

  // ── Alert ──
  const [alert, setAlert] = useState({
    open: false,
    type: "info",
    title: "",
    message: "",
    onConfirm: null,
  });

  const billNameRef = useRef(null);
  const invoiceNumberRef = useRef(null);
  const vendorFilterRef = useRef(null); // ← new
  const projectFilterRef = useRef(null); // ← new
  const invoiceStatusFilterRef = useRef(null); // ← new

  const showAlert = (type, title, message, opts = {}) =>
    setAlert({
      open: true,
      type,
      title,
      message,
      confirmText: opts.confirmText ?? "OK",
      cancelText: opts.cancelText,
      onConfirm:
        opts.onConfirm ?? (() => setAlert((p) => ({ ...p, open: false }))),
      onClose: () => setAlert((p) => ({ ...p, open: false })),
    });

  // ── Close filter on outside click ──
  useEffect(() => {
    const h = (e) => {
      const refs = [
        billNameRef,
        invoiceNumberRef,
        moNumberRef,
        vendorFilterRef,
        projectFilterRef,
        invoiceStatusFilterRef,
      ];
      if (!refs.some((r) => r.current?.contains(e.target))) setShowFilter(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const authH = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  // ── Fetch company default tax rate ──
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/company-profile/tax-rate`, {
          headers: authH(),
        });
        let rate = parseFloat(res.data.taxRate) || 0;
        // tax-rate route returns taxRate.toString(); normalize percent → fraction
        if (rate > 1) rate = rate / 100;
        setDefaultTaxRate(rate);
      } catch {
        setDefaultTaxRate(0);
      }
    })();
  }, []);

  // ── Fetch reference data — V3 projects only via optionsV2 ──
  useEffect(() => {
    (async () => {
      try {
        const [optsRes, vendRes, subRes] = await Promise.all([
          axios.get(`${API}/projects/optionsV2`, { headers: authH() }),
          axios.get(`${API}/accounts/vendors`, { headers: authH() }),
          axios.get(`${API}/users/subcontractors`, { headers: authH() }),
        ]);

        // optionsV2 returns all projects; keep version === 3 only
        const all = optsRes.data || [];
        const v3 = all.filter((p) => p.version === 3);
        setV3Projects(v3);
        setVendors(vendRes.data || []);
        setSubcontractors(subRes.data || []);
      } catch (err) {
        showAlert(
          "error",
          "Load Error",
          err.response?.data?.message || err.message,
        );
      }
    })();
  }, []);

  // ── Fetch filter vendor list per tab ──
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/bills3/vendors`, {
          headers: authH(),
          params: { processingStatus: activeTab },
        });
        setFilterVendors(res.data || []);
      } catch {}
    })();
  }, [activeTab]);

  const previewBulkMarkPaid = async () => {
    const invoiceNumbers = bulkPaidText
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);

    if (!invoiceNumbers.length) {
      showAlert(
        "warning",
        "Nothing to look up",
        "Paste at least one invoice number.",
      );
      return;
    }

    setBulkPaidPreviewing(true);
    setBulkPaidResult(null);
    setBulkPaidCommitted(null);
    try {
      const res = await axios.post(
        `${API}/bills3/bulk-mark-paid-by-invoice`,
        {
          invoiceNumbers,
          datePaid: bulkPaidDate2 || undefined,
          paymentFeePercent: bulkPaidFeePct || undefined,
          dryRun: true,
        },
        { headers: authH() },
      );
      setBulkPaidResult(res.data);
    } catch (err) {
      showAlert(
        "error",
        "Lookup Failed",
        err.response?.data?.message || err.message,
      );
    } finally {
      setBulkPaidPreviewing(false);
    }
  };

  const confirmBulkMarkPaid = async () => {
    if (!bulkPaidResult) return; // must preview first

    const invoiceNumbers = bulkPaidText
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);

    setBulkPaidConfirming(true);
    try {
      const res = await axios.post(
        `${API}/bills3/bulk-mark-paid-by-invoice`,
        {
          invoiceNumbers,
          datePaid: bulkPaidDate2 || undefined,
          paymentFeePercent: bulkPaidFeePct || undefined,
          dryRun: false,
        },
        { headers: authH() },
      );
      setBulkPaidCommitted(res.data);
      showAlert(
        "success",
        "Marked Paid",
        `${res.data.updatedCount} bill(s) marked paid.`,
      );
      fetchBills(currentPage);
    } catch (err) {
      showAlert(
        "error",
        "Bulk Mark Paid Failed",
        err.response?.data?.message || err.message,
      );
    } finally {
      setBulkPaidConfirming(false);
    }
  };

  // Any edit to the inputs invalidates the current preview so a stale
  // result can never be confirmed against a changed list/date.
  const handleBulkPaidTextChange = (v) => {
    setBulkPaidText(v);
    setBulkPaidResult(null);
    setBulkPaidCommitted(null);
  };
  const handleBulkPaidDateChange = (v) => {
    setBulkPaidDate2(v);
    setBulkPaidResult(null);
    setBulkPaidCommitted(null);
  };

  const handleBulkPaidFeeChange = (v) => {
    setBulkPaidFeePct(v);
    setBulkPaidResult(null);
    setBulkPaidCommitted(null);
  };

  const handleBulkUpdate = async (updates) => {
    if (!selectedIds.length) return;
    setBulkSaving(true);
    try {
      const res = await axios.put(
        `${API}/bills3/bulk`,
        { ids: selectedIds, updates },
        { headers: authH() },
      );
      showAlert("success", "Updated", `${res.data.modified} bill(s) updated.`);
      setSelectedIds([]);
      setBulkInvDate("");
      setBulkPaidDate(""); // ← new
      setBulkDueDate("");
      fetchBills(currentPage);
    } catch (err) {
      showAlert(
        "error",
        "Bulk Update Failed",
        err.response?.data?.message || err.message,
      );
    } finally {
      setBulkSaving(false);
    }
  };

  const handleBulkFee = async (markPaid) => {
    if (!selectedIds.length) return;
    const pct = parseFloat(bulkFeePct);
    if (!isFinite(pct) || pct < 0 || pct > 100) {
      showAlert("warning", "Invalid fee", "Enter a fee percent from 0 to 100.");
      return;
    }
    setBulkSaving(true);
    try {
      const res = await axios.post(
        `${API}/bills3/bulk-payment-fee`,
        {
          ids: selectedIds,
          percent: pct,
          markPaid,
          datePaid: markPaid ? bulkPaidDate || undefined : undefined,
        },
        { headers: authH() },
      );
      const locked = res.data.skippedLocked?.length
        ? ` ${res.data.skippedLocked.length} payroll-locked bill(s) were not marked paid.`
        : "";
      showAlert(
        "success",
        "Fee Applied",
        `${pct}% fee applied to ${res.data.updated} bill(s) — $${fmt(res.data.totalFee)} total.${locked}`,
      );
      setSelectedIds([]);
      setBulkFeePct("");
      fetchBills(currentPage);
    } catch (err) {
      showAlert(
        "error",
        "Apply Fee Failed",
        err.response?.data?.message || err.message,
      );
    } finally {
      setBulkSaving(false);
    }
  };

  // ── Main bills fetch ──
  const fetchBills = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/bills3`, {
          headers: authH(),
          params: {
            processingStatus: activeTab,
            page,
            limit: PAGE_SIZE,
            sort: sortField,
            direction: sortDir,
            billName: filterState.billName,
            invoiceNumber: filterState.invoiceNumber,
            vendorId: filterState.vendor,
            project: filterState.project,
            invoiceStatus: filterState.invoiceStatus,
            moNumber: filterState.moNumber, // ← new
          },
        });
        setBills(res.data.bills || []);
        setTotalItems(res.data.total || 0);
        setTotalPages(res.data.pages || 1);
      } catch (err) {
        showAlert(
          "error",
          "Load Error",
          err.response?.data?.message || err.message,
        );
      } finally {
        setLoading(false);
      }
    },
    [activeTab, sortField, sortDir, filterState],
  );

  useEffect(() => {
    fetchBills(currentPage);
  }, [fetchBills, currentPage]);

  // ── Load MOs for a project (lazy, cached) ──
  // Uses the materialOrders3 project endpoint directly.
  const loadMOs = async (projectId) => {
    if (!projectId || mosByProject[projectId]) return;
    try {
      const res = await axios.get(
        `${API}/materialOrders3/project/${projectId}`,
        { headers: authH() },
      );
      setMosByProject((p) => ({ ...p, [projectId]: res.data.orders || [] }));
    } catch {
      setMosByProject((p) => ({ ...p, [projectId]: [] }));
    }
  };

  // ── Filter helpers ──
  // Replace the existing handleFilterChange
  const handleFilterChange = (field, value) => {
    setFilterState((p) => ({ ...p, [field]: value }));
    setCurrentPage(1);
    // Text-input filters that stay open while the user types
    const textFilters = ["billName", "invoiceNumber", "moNumber"];
    if (!textFilters.includes(field)) setShowFilter(null);
  };

  const clearFilters = () => {
    setFilterState({
      billName: "",
      invoiceNumber: "",
      vendor: "",
      project: "",
      invoiceStatus: "",
      moNumber: "",
    });
    setCurrentPage(1);
    setShowFilter(null);
  };
  const hasFilters = Object.values(filterState).some(Boolean);

  // ── Sort ──
  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortField(field);
      setSortDir("desc");
    }
    setCurrentPage(1);
  };
  const SortIcon = ({ field }) =>
    sortField !== field ? (
      <span className="opacity-30 ml-1">↕</span>
    ) : (
      <span className="ml-1 text-orange-400">
        {sortDir === "asc" ? "↑" : "↓"}
      </span>
    );

  // ── Selection ──
  const toggleAll = (e) =>
    setSelectedIds(e.target.checked ? bills.map((b) => b._id) : []);
  const toggleOne = (id) =>
    setSelectedIds((p) =>
      p.includes(id) ? p.filter((x) => x !== id) : [...p, id],
    );

  // ── Confirm bill ──
  const confirmBill = async (billId) => {
    try {
      await axios.post(
        `${API}/bills3/${billId}/confirm`,
        {},
        { headers: authH() },
      );
      showAlert(
        "success",
        "Confirmed",
        "Bill confirmed and marked as processed.",
      );
      fetchBills(currentPage);
      if (selectedBill?._id === billId) setSelectedBill(null);
    } catch (err) {
      showAlert(
        "error",
        "Confirm Failed",
        err.response?.data?.message || err.message,
      );
    }
  };

  // ── Revert bill ──
  const revertBill = async (billId) => {
    showAlert("warning", "Revert Bill", "Move this bill back to pending?", {
      confirmText: "Revert",
      cancelText: "Cancel",
      onConfirm: async () => {
        setAlert((p) => ({ ...p, open: false }));
        try {
          await axios.post(
            `${API}/bills3/${billId}/revert`,
            {},
            { headers: authH() },
          );
          showAlert("success", "Reverted", "Bill moved back to pending.");
          fetchBills(currentPage);
          if (selectedBill?._id === billId) setSelectedBill(null);
        } catch (err) {
          showAlert(
            "error",
            "Revert Failed",
            err.response?.data?.message || err.message,
          );
        }
      },
    });
  };

  const runBulkMarkPaidByInvoice = async (dryRun = false) => {
    const invoiceNumbers = bulkPaidText
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);

    if (!invoiceNumbers.length) {
      showAlert(
        "warning",
        "Nothing to look up",
        "Paste at least one invoice number.",
      );
      return;
    }

    setBulkPaidRunning(true);
    setBulkPaidResult(null);
    try {
      const res = await axios.post(
        `${API}/bills3/bulk-mark-paid-by-invoice`,
        {
          invoiceNumbers,
          datePaid: bulkPaidDate2 || undefined,
          dryRun,
        },
        { headers: authH() },
      );
      setBulkPaidResult(res.data);
      if (!dryRun) fetchBills(currentPage);
    } catch (err) {
      showAlert(
        "error",
        "Bulk Mark Paid Failed",
        err.response?.data?.message || err.message,
      );
    } finally {
      setBulkPaidRunning(false);
    }
  };

  // ── Delete bill ──
  const deleteBill = async (billId) => {
    showAlert(
      "warning",
      "Delete Bill",
      "Delete this pending bill? This cannot be undone.",
      {
        confirmText: "Delete",
        cancelText: "Cancel",
        onConfirm: async () => {
          setAlert((p) => ({ ...p, open: false }));
          try {
            await axios.delete(`${API}/bills3/${billId}`, { headers: authH() });
            showAlert("success", "Deleted", "Bill deleted.");
            fetchBills(currentPage);
            if (selectedBill?._id === billId) setSelectedBill(null);
          } catch (err) {
            showAlert(
              "error",
              "Delete Failed",
              err.response?.data?.message || err.message,
            );
          }
        },
      },
    );
  };

  // ── Open edit modal ──
  const openEdit = (bill) => {
    const projectId = bill.project?._id || bill.project;
    // Eagerly load MOs for this project
    loadMOs(projectId);

    setSelectedBill({
      ...bill,
      _projectId: projectId,
      _isWO: !!bill.relatedWO3,
      relatedPO3Id: bill.relatedPO3?._id || bill.relatedPO3 || "",
      relatedMO3Id: bill.relatedMO3?._id || bill.relatedMO3 || "",
      relatedWO3Id: bill.relatedWO3?._id || bill.relatedWO3 || "",
      invoiceNumbers: bill.invoiceNumber
        ? bill.invoiceNumber.split(/\s+/).filter(Boolean)
        : [""],
      credits: (bill.credits || []).map((c) => ({ ...c, _newFile: null })),
      payToId: bill.payTo?._id || bill.payTo || "",
      subContractorId: bill.subContractor?._id || bill.subContractor || "",
      invoiceDate: bill.invoiceDate ? bill.invoiceDate.split("T")[0] : "",
      datePaid: bill.datePaid ? bill.datePaid.split("T")[0] : "",
      dueDate: bill.dueDate ? bill.dueDate.split("T")[0] : "",
    });
    setNewBillFiles([]);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Save edit ──
  const saveEdit = async () => {
    if (!selectedBill) return;
    setEditSaving(true);
    try {
      const joinedInvoiceNumbers = (selectedBill.invoiceNumbers || [])
        .map((v) => v.trim())
        .filter(Boolean)
        .join(" ");
      const effectiveInvoiceNumber =
        joinedInvoiceNumbers || selectedBill.invoiceNumber || "";
      const effectiveBillName =
        (selectedBill.billName || "").trim() || effectiveInvoiceNumber;

      const fd = new FormData();
      fd.append("billName", effectiveBillName);
      fd.append("invoiceNumber", effectiveInvoiceNumber);
      fd.append("invoiceDate", selectedBill.invoiceDate || "");
      if (selectedBill.dueDate) fd.append("dueDate", selectedBill.dueDate);
      if (selectedBill.datePaid) fd.append("datePaid", selectedBill.datePaid);
      fd.append("invoiceStatus", selectedBill.invoiceStatus || "unpaid");
      fd.append(
        "project",
        selectedBill.projectId || selectedBill._projectId || "",
      );
      fd.append("subtotal", selectedBill.subtotal ?? 0);
      fd.append("tax", selectedBill.tax ?? 0);
      fd.append("total", selectedBill.total ?? 0);
      fd.append("description", selectedBill.description || "");
      fd.append("notes", selectedBill.notes || "");

      // Payment processing fee
      fd.append("paymentFeePercent", selectedBill.paymentFeePercent || 0);
      fd.append("paymentFeeAmount", selectedBill.paymentFeeAmount || 0);
      fd.append("paymentFeeNote", selectedBill.paymentFeeNote || "");

      if (selectedBill._isWO) {
        fd.append("relatedWO3", selectedBill.relatedWO3Id || "");
        fd.append("subContractor", selectedBill.subContractorId || "");
        fd.append("relatedPO3", "");
        fd.append("relatedMO3", "");
        fd.append("payTo", "");
      } else {
        fd.append("relatedPO3", selectedBill.relatedPO3Id || "");
        fd.append("relatedMO3", selectedBill.relatedMO3Id || "");
        fd.append("payTo", selectedBill.payToId || "");
        fd.append("relatedWO3", "");
        fd.append("subContractor", "");
      }

      const existing = (selectedBill.attachments || []).filter((a) => a.url);
      fd.append("existingAttachments", JSON.stringify(existing));
      newBillFiles.forEach((f) => fd.append("billFiles", f));

      const creditsPayload = (selectedBill.credits || []).map(
        ({ _newFile, ...c }) => c,
      );
      fd.append("creditsData", JSON.stringify(creditsPayload));
      (selectedBill.credits || []).forEach((c, i) => {
        if (c._newFile) fd.append(`creditFile_${i}`, c._newFile);
      });

      await axios.put(`${API}/bills3/${selectedBill._id}`, fd, {
        headers: { ...authH(), "Content-Type": "multipart/form-data" },
      });

      showAlert("success", "Saved", "Bill updated successfully.");
      setSelectedBill(null);
      fetchBills(currentPage);
    } catch (err) {
      showAlert(
        "error",
        "Save Failed",
        err.response?.data?.message || err.message,
      );
    } finally {
      setEditSaving(false);
    }
  };

  // ── Open create modal ──
  const openCreate = () => {
    setCreateForm({
      billName: "",
      invoiceNumbers: [""],
      invoiceDate: "",
      dueDate: "",
      credits: [],
      datePaid: "",
      invoiceStatus: "unpaid",
      projectId: "",
      isWO: false,
      relatedPO3Id: "",
      relatedMO3Id: "",
      relatedWO3Id: "",
      payToId: "",
      subContractorId: "",
      subtotal: "",
      tax: "",
      total: "",
      description: "",
      notes: "",
    });
    setCreateBillFiles([]);
    setShowCreate(true);
  };

  // ── Save create ──
  const saveCreate = async () => {
    const joinedInvoiceNumbers = (createForm.invoiceNumbers || [])
      .map((v) => v.trim())
      .filter(Boolean)
      .join(" ");

    if (
      !joinedInvoiceNumbers ||
      !createForm.invoiceDate ||
      !createForm.projectId
    ) {
      showAlert(
        "error",
        "Missing Fields",
        "At least one invoice #, invoice date, and project are required.",
      );
      return;
    }

    setCreateSaving(true);
    try {
      const effectiveBillName =
        (createForm.billName || "").trim() || joinedInvoiceNumbers;

      const fd = new FormData();
      fd.append("billName", effectiveBillName);
      fd.append("invoiceNumber", joinedInvoiceNumbers);
      fd.append("invoiceDate", createForm.invoiceDate);
      if (createForm.dueDate) fd.append("dueDate", createForm.dueDate);
      if (createForm.datePaid) fd.append("datePaid", createForm.datePaid);
      fd.append("invoiceStatus", createForm.invoiceStatus || "unpaid");
      fd.append("project", createForm.projectId);
      fd.append("subtotal", createForm.subtotal || 0);
      fd.append("tax", createForm.tax || 0);
      fd.append("total", createForm.total || 0);
      fd.append("description", createForm.description || "");
      fd.append("notes", createForm.notes || "");

      if (createForm.isWO) {
        fd.append("relatedWO3", createForm.relatedWO3Id || "");
        fd.append("subContractor", createForm.subContractorId || "");
      } else {
        fd.append("relatedPO3", createForm.relatedPO3Id || "");
        fd.append("relatedMO3", createForm.relatedMO3Id || "");
        fd.append("payTo", createForm.payToId || "");
      }

      createBillFiles.forEach((f) => fd.append("billFiles", f));

      const creditsPayload = (createForm.credits || []).map(
        ({ _newFile, ...c }) => ({
          ...c,
          attachment: null, // new bill — no existing Cloudinary URL yet
        }),
      );
      fd.append("creditsData", JSON.stringify(creditsPayload));
      (createForm.credits || []).forEach((c, i) => {
        if (c._newFile) fd.append(`creditFile_${i}`, c._newFile);
      });

      await axios.post(`${API}/bills3`, fd, {
        headers: { ...authH(), "Content-Type": "multipart/form-data" },
      });

      showAlert("success", "Created", "Bill created successfully.");
      setShowCreate(false);
      setCreateForm(null);
      fetchBills(1);
      setCurrentPage(1);
    } catch (err) {
      showAlert(
        "error",
        "Create Failed",
        err.response?.data?.message || err.message,
      );
    } finally {
      setCreateSaving(false);
    }
  };

  // ── Export to Excel ──
  const exportExcel = () => {
    if (!bills.length) {
      showAlert("warning", "Nothing to Export", "No bills in current view.");
      return;
    }
    const rows = bills.map((b) => ({
      "Bill Name": b.billName || "",
      "Invoice #": b.invoiceNumber || "",
      "Vendor / Sub":
        b.subContractor?.displayName || b.payTo?.companyName || "—",
      Project: b.project?.projectName || "—",
      "PO3 #": b.relatedPO3?.poNumber || "—",
      "WO3 #": b.relatedWO3?.woNumber || "—",
      "MO3 #": b.relatedMO3?.orderNumber || "—",
      "MO Status": b.relatedMO3?.status || "—",
      "Invoice Date": b.invoiceDate
        ? new Date(b.invoiceDate).toLocaleDateString()
        : "",
      "Invoice Status": b.invoiceStatus || "",
      Processing: b.processingStatus || "",
      Subtotal: `$${fmt(b.subtotal)}`,
      Tax: `$${fmt(b.tax)}`,
      Total: `$${fmt(b.total)}`,
      "Pay Fee %": b.paymentFeePercent || 0,
      "Pay Fee $": b.paymentFeeAmount || 0,
      "Date Paid": b.datePaid ? new Date(b.datePaid).toLocaleDateString() : "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      ws,
      activeTab === "pending" ? "Pending" : "Processed",
    );
    XLSX.writeFile(
      wb,
      `Bills3_${activeTab}_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  };

  // ── Option builders — all scoped to V3 projects ──
  // v3Projects comes from optionsV2 filtered to version === 3,
  // so .purchaseOrders and .workOrders are already populated inline.
  const projectOptions = v3Projects.map((p) => ({
    value: p._id,
    label: p.projectName,
  }));
  const vendorOptions = vendors.map((v) => ({
    value: v._id,
    label: v.companyName,
  }));
  const subOptions = subcontractors.map((s) => ({
    value: s._id,
    label: s.displayName || s.companyName || "Unnamed",
  }));
  const filterVendorOptions = filterVendors.map((v) => ({
    value: v._id,
    label: `${v.name} (${v.type})`,
  }));

  const getPOOptions = (projectId) => {
    const proj = v3Projects.find((p) => String(p._id) === String(projectId));
    return (proj?.purchaseOrders || []).map((po) => ({
      value: po._id,
      label: `PO ${po.poNumber}${po.title ? ` — ${po.title}` : ""}`,
    }));
  };

  const getWOOptions = (projectId) => {
    const proj = v3Projects.find((p) => String(p._id) === String(projectId));
    return (proj?.workOrders || []).map((wo) => ({
      value: wo._id,
      label: `WO ${wo.woNumber}${wo.title ? ` — ${wo.title}` : ""}`,
    }));
  };

  // MOs are lazy-loaded from the MO3 endpoint and cached in mosByProject.
  // They are NOT pre-filtered by PO here — the user picks the MO from all
  // non-void orders on the project; the PO field is auto-filled from the MO.
  const getMOOptions = (projectId) => {
    const mos = mosByProject[projectId] || [];
    return mos
      .filter((mo) => mo.status !== "void")
      .map((mo) => {
        const poRef = mo.purchaseOrder;
        const poNum = poRef?.poNumber || "";
        return {
          value: mo._id,
          label: `MO ${mo.orderNumber}${poNum ? ` · PO ${poNum}` : ""} (${(mo.status || "").toUpperCase()})`,
          status: mo.status,
          poId: poRef?._id || poRef || null,
          poNumber: poNum,
        };
      })
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  };

  // ── Input class ──
  const inputCls =
    "block w-full rounded-md bg-gray-900 border border-gray-700 text-gray-100 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm py-2 px-3 transition-colors placeholder-gray-600";
  const labelCls =
    "block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wide";

  return (
    <div className="mx-auto px-4 py-6 mt-8">
      <AlertDialog
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        confirmText={alert.confirmText}
        cancelText={alert.cancelText}
        onClose={alert.onClose}
        onConfirm={alert.onConfirm}
      />

      {/* ════ Main Table Card ════ */}
      <div className="bg-gray-900 rounded-xl border border-gray-800 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-800 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-white tracking-tight">
              Bills{" "}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {loading
                ? "Loading…"
                : `${totalItems.toLocaleString()} ${activeTab} bill${totalItems !== 1 ? "s" : ""}`}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-amber-400 bg-amber-900/30 border border-amber-700/50 hover:bg-amber-900/50 transition-colors"
              >
                ✕ Clear filters
              </button>
            )}
            <button
              onClick={exportExcel}
              disabled={!bills.length || loading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-white bg-emerald-700 hover:bg-emerald-600 disabled:opacity-40 transition-colors"
            >
              ↓ Export
            </button>
            <button
              onClick={() => navigate("/bills3/upload")}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-500 transition-colors shadow-sm"
            >
              ↑ Upload Invoices
            </button>
            <button
              onClick={() => setShowScanner(true)}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-semibold text-white bg-teal-600 hover:bg-teal-500 transition-colors shadow-sm"
            >
              📋 Scan Statement
            </button>
            <button
              onClick={openCreate}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-semibold text-white bg-orange-500 hover:bg-orange-400 transition-colors shadow-sm"
            >
              + Add Bill
            </button>
            <button
              onClick={() => {
                setBulkPaidText("");
                setBulkPaidResult(null);
                setBulkPaidCommitted(null);
                setBulkPaidDate2("");
                setBulkPaidFeePct("");
                setShowBulkPaid(true);
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-white bg-emerald-700 hover:bg-emerald-600 transition-colors"
            >
              ✓ Bulk Mark Paid
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-800">
          {["pending", "processed"].map((tab) => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                setCurrentPage(1);
                setSelectedIds([]);
              }}
              className={cls(
                "px-6 py-3 text-sm font-medium transition-colors relative",
                activeTab === tab
                  ? "text-orange-400"
                  : "text-gray-500 hover:text-gray-300",
              )}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              {activeTab === tab && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-400" />
              )}
            </button>
          ))}
        </div>

        {/* ── Bulk Action Bar ── */}
        {selectedIds.length > 0 && (
          <div className="border-b border-orange-700/40 bg-orange-950/20 px-6 py-3 flex flex-wrap items-center gap-3">
            <span className="text-sm font-semibold text-orange-400">
              {selectedIds.length} selected
            </span>
            <div className="h-4 w-px bg-gray-700" />

            {/* Status — "Paid" also stamps Date Paid (from the field below, or today if empty) in the same request */}
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-gray-500 uppercase tracking-wide">
                Status:
              </span>
              {["paid", "unpaid", "partial"].map((s) => (
                <button
                  key={s}
                  disabled={bulkSaving}
                  onClick={() => {
                    const updates = { invoiceStatus: s };
                    if (s === "paid") {
                      updates.datePaid =
                        bulkPaidDate || new Date().toISOString().slice(0, 10);
                    } else if (s === "unpaid") {
                      // Reverting to unpaid clears any stamped payment date
                      updates.datePaid = null;
                    }
                    handleBulkUpdate(updates);
                  }}
                  className="px-2.5 py-1 text-xs rounded font-medium border transition-colors disabled:opacity-40
    bg-gray-800 border-gray-700 text-gray-300 hover:border-orange-500 hover:text-orange-300"
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
            <div className="h-4 w-px bg-gray-700" />

            {/* Invoice Date */}
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-gray-500 uppercase tracking-wide">
                Inv. Date:
              </span>
              <input
                type="date"
                value={bulkInvDate}
                onChange={(e) => setBulkInvDate(e.target.value)}
                className="rounded bg-gray-800 border border-gray-700 text-gray-200 text-xs py-1 px-2 focus:border-orange-500 focus:outline-none"
              />
              <button
                disabled={!bulkInvDate || bulkSaving}
                onClick={() => handleBulkUpdate({ invoiceDate: bulkInvDate })}
                className="px-2.5 py-1 text-xs rounded font-medium bg-orange-600 hover:bg-orange-500 text-white transition-colors disabled:opacity-40"
              >
                Apply
              </button>
            </div>
            <div className="h-4 w-px bg-gray-700" />

            {/* Due Date */}
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-gray-500 uppercase tracking-wide">
                Due Date:
              </span>
              <input
                type="date"
                value={bulkDueDate}
                onChange={(e) => setBulkDueDate(e.target.value)}
                className="rounded bg-gray-800 border border-gray-700 text-gray-200 text-xs py-1 px-2 focus:border-orange-500 focus:outline-none"
              />
              <button
                disabled={!bulkDueDate || bulkSaving}
                onClick={() => handleBulkUpdate({ dueDate: bulkDueDate })}
                className="px-2.5 py-1 text-xs rounded font-medium bg-orange-600 hover:bg-orange-500 text-white transition-colors disabled:opacity-40"
              >
                Apply
              </button>
            </div>
            <div className="h-4 w-px bg-gray-700" />

            {/* Date Paid — standalone Apply for correcting/backfilling without touching status,
       and also feeds the "Paid" status button above when set */}
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-gray-500 uppercase tracking-wide">
                Date Paid:
              </span>
              <input
                type="date"
                value={bulkPaidDate}
                onChange={(e) => setBulkPaidDate(e.target.value)}
                className="rounded bg-gray-800 border border-gray-700 text-gray-200 text-xs py-1 px-2 focus:border-orange-500 focus:outline-none"
              />
              <button
                disabled={!bulkPaidDate || bulkSaving}
                onClick={() => handleBulkUpdate({ datePaid: bulkPaidDate })}
                className="px-2.5 py-1 text-xs rounded font-medium bg-orange-600 hover:bg-orange-500 text-white transition-colors disabled:opacity-40"
              >
                Apply
              </button>
            </div>
            <div className="h-4 w-px bg-gray-700" />

            {/* Payment processing fee */}
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-gray-500 uppercase tracking-wide">
                Pay Fee %:
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                placeholder="e.g. 2.9"
                value={bulkFeePct}
                onChange={(e) => setBulkFeePct(e.target.value)}
                className="w-20 rounded bg-gray-800 border border-gray-700 text-gray-200 text-xs py-1 px-2 focus:border-orange-500 focus:outline-none"
              />
              <button
                disabled={bulkFeePct === "" || bulkSaving}
                onClick={() => handleBulkFee(false)}
                className="px-2.5 py-1 text-xs rounded font-medium bg-gray-800 border border-gray-700 text-gray-300 hover:border-orange-500 hover:text-orange-300 transition-colors disabled:opacity-40"
              >
                Apply Fee
              </button>
              <button
                disabled={bulkFeePct === "" || bulkSaving}
                onClick={() => handleBulkFee(true)}
                className="px-2.5 py-1 text-xs rounded font-medium bg-orange-600 hover:bg-orange-500 text-white transition-colors disabled:opacity-40"
              >
                Fee + Mark Paid
              </button>
            </div>
            <button
              onClick={() => setSelectedIds([])}
              className="ml-auto text-xs text-gray-500 hover:text-gray-300 transition-colors"
            >
              ✕ Deselect all
            </button>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <tr className="border-b border-gray-800">
              <th className="px-4 py-3 w-8">
                <input
                  type="checkbox"
                  checked={
                    selectedIds.length === bills.length && bills.length > 0
                  }
                  onChange={toggleAll}
                  className="rounded border-gray-600 text-orange-400 focus:ring-orange-500 bg-gray-800"
                />
              </th>
              {/* Bill Name */}
              <Th
                active={!!filterState.billName}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setShowFilter((p) => (p === "billName" ? null : "billName"));
                }}
              >
                <span className="relative inline-flex items-center gap-1">
                  Bill Name
                  {filterState.billName && (
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-400 inline-block" />
                  )}
                  {showFilter === "billName" && (
                    <div
                      ref={billNameRef}
                      onMouseDown={(e) => e.stopPropagation()}
                      className="absolute left-0 top-6 z-50 bg-gray-800 border border-gray-700 rounded-lg shadow-xl p-3 w-52"
                    >
                      <input
                        autoFocus
                        type="text"
                        placeholder="Search…"
                        value={filterState.billName}
                        onChange={(e) =>
                          handleFilterChange("billName", e.target.value)
                        }
                        className="w-full rounded bg-gray-900 border border-gray-700 text-gray-200 text-xs py-1.5 px-2 focus:outline-none focus:border-orange-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  )}
                </span>
              </Th>
              {/* Invoice # */}
              <Th
                active={!!filterState.invoiceNumber}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setShowFilter((p) =>
                    p === "invoiceNumber" ? null : "invoiceNumber",
                  );
                }}
              >
                <span className="relative inline-flex items-center gap-1">
                  Invoice #
                  {filterState.invoiceNumber && (
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-400 inline-block" />
                  )}
                  {showFilter === "invoiceNumber" && (
                    <div
                      ref={invoiceNumberRef}
                      onMouseDown={(e) => e.stopPropagation()}
                      className="absolute left-0 top-6 z-50 bg-gray-800 border border-gray-700 rounded-lg shadow-xl p-3 w-52"
                    >
                      <input
                        autoFocus
                        type="text"
                        placeholder="Search…"
                        value={filterState.invoiceNumber}
                        onChange={(e) =>
                          handleFilterChange("invoiceNumber", e.target.value)
                        }
                        className="w-full rounded bg-gray-900 border border-gray-700 text-gray-200 text-xs py-1.5 px-2 focus:outline-none focus:border-orange-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  )}
                </span>
              </Th>
              {/* Vendor */}

              <Th
                active={!!filterState.vendor}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setShowFilter((p) => (p === "vendor" ? null : "vendor"));
                }}
              >
                <span className="relative inline-flex items-center gap-1">
                  Vendor / Sub
                  {filterState.vendor && (
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-400 inline-block" />
                  )}
                  {showFilter === "vendor" && (
                    <div
                      ref={vendorFilterRef}
                      onMouseDown={(e) => e.stopPropagation()}
                      className="absolute left-0 top-6 z-50 bg-gray-800 border border-gray-700 rounded-lg shadow-xl p-3 w-80"
                    >
                      <Select
                        options={[
                          { value: "", label: "All" },
                          ...filterVendorOptions,
                        ]}
                        value={
                          filterVendorOptions.find(
                            (o) => o.value === filterState.vendor,
                          ) || { value: "", label: "All" }
                        }
                        onChange={(s) =>
                          handleFilterChange("vendor", s?.value || "")
                        }
                        styles={selectStyles}
                        isClearable
                        isSearchable
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        placeholder="Search…"
                      />
                    </div>
                  )}
                </span>
              </Th>
              {/* Project */}
              <Th
                active={!!filterState.project}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setShowFilter((p) => (p === "project" ? null : "project"));
                }}
              >
                <span className="relative inline-flex items-center gap-1">
                  Project
                  {filterState.project && (
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-400 inline-block" />
                  )}
                  {showFilter === "project" && (
                    <div
                      ref={projectFilterRef}
                      onMouseDown={(e) => e.stopPropagation()}
                      className="absolute left-0 top-6 z-50 bg-gray-800 border border-gray-700 rounded-lg shadow-xl p-3 w-72"
                    >
                      <Select
                        options={[
                          { value: "", label: "All Projects" },
                          ...projectOptions,
                        ]}
                        value={
                          projectOptions.find(
                            (o) => o.value === filterState.project,
                          ) || { value: "", label: "All Projects" }
                        }
                        onChange={(s) =>
                          handleFilterChange("project", s?.value || "")
                        }
                        styles={selectStyles}
                        isClearable
                        isSearchable
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        placeholder="Search…"
                      />
                    </div>
                  )}
                </span>
              </Th>
              <Th onClick={() => toggleSort("invoiceDate")}>
                Inv. Date <SortIcon field="invoiceDate" />
              </Th>
              {/* MO3 — now filterable by order number */}
              <Th
                active={!!filterState.moNumber}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setShowFilter((p) => (p === "moNumber" ? null : "moNumber"));
                }}
              >
                <span className="relative inline-flex items-center gap-1">
                  MO / Status
                  {filterState.moNumber && (
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-400 inline-block" />
                  )}
                  {showFilter === "moNumber" && (
                    <div
                      ref={moNumberRef}
                      onMouseDown={(e) => e.stopPropagation()}
                      className="absolute left-0 top-6 z-50 bg-gray-800 border border-gray-700 rounded-lg shadow-xl p-3 w-44"
                    >
                      <input
                        autoFocus
                        type="text"
                        placeholder="MO #…"
                        value={filterState.moNumber}
                        onChange={(e) =>
                          handleFilterChange("moNumber", e.target.value)
                        }
                        className="w-full rounded bg-gray-900 border border-gray-700 text-gray-200 text-xs py-1.5 px-2 focus:outline-none focus:border-orange-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  )}
                </span>
              </Th>
              {/* Invoice Status */}
              <Th
                active={!!filterState.invoiceStatus}
                onMouseDown={(e) => {
                  e.stopPropagation();

                  setShowFilter((p) =>
                    p === "invoiceStatus" ? null : "invoiceStatus",
                  );
                }}
              >
                <span className="relative inline-flex items-center gap-1">
                  Status
                  {filterState.invoiceStatus && (
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-400 inline-block" />
                  )}
                  {showFilter === "invoiceStatus" && (
                    <div
                      ref={invoiceStatusFilterRef}
                      onMouseDown={(e) => e.stopPropagation()}
                      className="absolute left-0 top-6 z-50 bg-gray-800 border border-gray-700 rounded-lg shadow-xl p-3 w-48"
                    >
                      <Select
                        options={[
                          { value: "", label: "All" },
                          ...["unpaid", "paid", "partial"].map((s) => ({
                            value: s,
                            label: s.charAt(0).toUpperCase() + s.slice(1),
                          })),
                        ]}
                        value={
                          [
                            { value: "", label: "All" },
                            ...["unpaid", "paid", "partial"].map((s) => ({
                              value: s,
                              label: s.charAt(0).toUpperCase() + s.slice(1),
                            })),
                          ].find(
                            (o) => o.value === filterState.invoiceStatus,
                          ) || { value: "", label: "All" }
                        }
                        onChange={(s) =>
                          handleFilterChange("invoiceStatus", s?.value || "")
                        }
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={selectStyles}
                      />
                    </div>
                  )}
                </span>
              </Th>
              <Th onClick={() => toggleSort("subtotal")} className="text-right">
                Subtotal <SortIcon field="subtotal" />
              </Th>
              <Th>Attachments</Th>
              <th className="px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                Actions
              </th>
            </tr>

            <tbody className="divide-y divide-gray-800/60">
              {loading ? (
                <tr>
                  <td
                    colSpan={11}
                    className="px-6 py-12 text-center text-gray-500 text-sm"
                  >
                    Loading bills…
                  </td>
                </tr>
              ) : bills.length === 0 ? (
                <tr>
                  <td
                    colSpan={11}
                    className="px-6 py-12 text-center text-gray-500 text-sm"
                  >
                    No {activeTab} bills found.
                  </td>
                </tr>
              ) : (
                bills.map((bill) => (
                  <tr
                    key={bill._id}
                    onClick={() => openEdit(bill)}
                    className="hover:bg-gray-800/60 transition-colors cursor-pointer group"
                  >
                    <td
                      className="px-4 py-3"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(bill._id)}
                        onChange={() => toggleOne(bill._id)}
                        className="rounded border-gray-600 text-orange-400 focus:ring-orange-500 bg-gray-800"
                      />
                    </td>
                    <td className="px-3 py-3 text-gray-200 max-w-[160px]">
                      <div
                        className="truncate font-medium"
                        title={bill.billName}
                      >
                        {bill.billName || "—"}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-gray-400 font-mono text-xs">
                      {bill.invoiceNumber || "—"}
                    </td>
                    <td className="px-3 py-3 text-gray-300 max-w-[140px]">
                      <div className="truncate text-xs">
                        {bill.subContractor?.displayName ||
                          bill.payTo?.companyName ||
                          "—"}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-gray-300 max-w-[140px]">
                      <div className="truncate text-xs">
                        {bill.project?.projectName || "—"}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-gray-400 text-xs whitespace-nowrap">
                      {bill.invoiceDate
                        ? new Date(bill.invoiceDate).toLocaleDateString()
                        : "—"}
                    </td>

                    <td className="px-3 py-3 text-xs">
                      {bill.relatedMO3 ? (
                        <div className="flex flex-col">
                          <span className="text-gray-400 font-mono">
                            MO {bill.relatedMO3.orderNumber}
                          </span>
                          <MoStatusBadge status={bill.relatedMO3.status} />
                        </div>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <InvoiceStatusBadge status={bill.invoiceStatus} />
                    </td>
                    <td className="px-3 py-3 text-right text-gray-200 font-medium text-xs tabular-nums">
                      ${fmt(bill.subtotal)}
                      {bill.paymentFeeAmount > 0 && (
                        <div
                          className="text-[10px] text-amber-400 font-normal"
                          title={`${bill.paymentFeePercent}% payment fee`}
                        >
                          + ${fmt(bill.paymentFeeAmount)} fee
                        </div>
                      )}
                    </td>
                    <td
                      className="px-3 py-3"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {(bill.attachments || []).length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {bill.attachments.slice(0, 2).map((a, i) => (
                            <a
                              key={i}
                              href={a.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[10px] text-blue-400 hover:text-blue-300 underline truncate max-w-[80px]"
                            >
                              {a.name}
                            </a>
                          ))}
                          {bill.attachments.length > 2 && (
                            <span className="text-[10px] text-gray-500">
                              +{bill.attachments.length - 2}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-600 text-xs">—</span>
                      )}
                    </td>
                    <td
                      className="px-4 py-3 text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => openEdit(bill)}
                          className="text-xs text-orange-400 hover:text-orange-300 font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // Eagerly load MOs so the panel has data immediately
                            const pid = bill.project?._id || bill.project;
                            if (pid) loadMOs(pid);
                            setReviewBill(bill);
                          }}
                          className="text-xs text-indigo-400 hover:text-indigo-300 font-medium"
                        >
                          Review
                        </button>
                        {activeTab === "pending" && (
                          <>
                            <button
                              onClick={() => confirmBill(bill._id)}
                              className="text-xs text-emerald-400 hover:text-emerald-300 font-medium"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => deleteBill(bill._id)}
                              className="text-xs text-red-500 hover:text-red-400"
                            >
                              <FaTrash />
                            </button>
                          </>
                        )}
                        {activeTab === "processed" && (
                          <button
                            onClick={() => revertBill(bill._id)}
                            className="text-xs text-amber-400 hover:text-amber-300 font-medium"
                          >
                            Revert
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-800 flex items-center justify-between">
          <p className="text-xs text-gray-500">
            Showing {bills.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1}
            –{Math.min(currentPage * PAGE_SIZE, totalItems)} of{" "}
            {totalItems.toLocaleString()}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 rounded-md text-xs font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 border border-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              ← Prev
            </button>
            <span className="text-xs text-gray-400 px-2">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 rounded-md text-xs font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 border border-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next →
            </button>
          </div>
        </div>
      </div>

      {/* ════ Edit Modal ════ */}
      {selectedBill && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm overflow-y-auto py-8 px-4">
          <div className="bg-gray-900 border border-gray-800 rounded-xl shadow-2xl w-full max-w-3xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
              <div>
                <h3 className="text-base font-semibold text-white">
                  {selectedBill.processingStatus === "pending"
                    ? "Edit Pending Bill"
                    : "Edit Processed Bill"}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {selectedBill.billName}
                </p>
              </div>
              <button
                onClick={() => setSelectedBill(null)}
                className="text-gray-500 hover:text-gray-300 transition-colors text-lg leading-none"
              >
                ✕
              </button>
            </div>

            <BillForm
              form={selectedBill}
              setForm={setSelectedBill}
              isCreate={false}
              projectOptions={projectOptions}
              vendorOptions={vendorOptions}
              subOptions={subOptions}
              vendors={vendors}
              v3Projects={v3Projects}
              getPOOptions={getPOOptions}
              getWOOptions={getWOOptions}
              getMOOptions={getMOOptions}
              loadMOs={loadMOs}
              selectStyles={selectStyles}
              inputCls={inputCls}
              labelCls={labelCls}
              createBillFiles={createBillFiles}
              setCreateBillFiles={setCreateBillFiles}
              newBillFiles={newBillFiles}
              setNewBillFiles={setNewBillFiles}
              setSelectedBill={setSelectedBill}
              defaultTaxRate={defaultTaxRate}
            />
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-800">
              <button
                onClick={() => setSelectedBill(null)}
                className="px-4 py-2 rounded-md text-sm font-medium text-gray-400 hover:text-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveEdit}
                disabled={editSaving}
                className="px-5 py-2 rounded-md text-sm font-semibold text-white bg-orange-500 hover:bg-orange-400 disabled:opacity-50 transition-colors"
              >
                {editSaving ? "Saving…" : "Save Changes"}
              </button>
              {selectedBill.processingStatus === "pending" && (
                <button
                  onClick={() => confirmBill(selectedBill._id)}
                  className="px-5 py-2 rounded-md text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-500 transition-colors"
                >
                  ✓ Confirm Bill
                </button>
              )}
              {selectedBill.processingStatus === "processed" && (
                <button
                  onClick={() => revertBill(selectedBill._id)}
                  className="px-5 py-2 rounded-md text-sm font-semibold text-white bg-amber-600 hover:bg-amber-500 transition-colors"
                >
                  ↩ Revert to Pending
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ════ Create Modal ════ */}
      {showCreate && createForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm overflow-y-auto py-8 px-4">
          <div className="bg-gray-900 border border-gray-800 rounded-xl shadow-2xl w-full max-w-3xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
              <h3 className="text-base font-semibold text-white">
                Add New Bill{" "}
                <span className="text-orange-400 text-sm font-normal">
                  (V3)
                </span>
              </h3>
              <button
                onClick={() => setShowCreate(false)}
                className="text-gray-500 hover:text-gray-300 transition-colors text-lg leading-none"
              >
                ✕
              </button>
            </div>

            <BillForm
              form={createForm}
              setForm={setCreateForm}
              isCreate={true}
              projectOptions={projectOptions}
              vendorOptions={vendorOptions}
              subOptions={subOptions}
              vendors={vendors}
              v3Projects={v3Projects}
              getPOOptions={getPOOptions}
              getWOOptions={getWOOptions}
              getMOOptions={getMOOptions}
              loadMOs={loadMOs}
              selectStyles={selectStyles}
              inputCls={inputCls}
              labelCls={labelCls}
              createBillFiles={createBillFiles}
              setCreateBillFiles={setCreateBillFiles}
              newBillFiles={newBillFiles}
              setNewBillFiles={setNewBillFiles}
              setSelectedBill={setSelectedBill}
              defaultTaxRate={defaultTaxRate}
            />

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-800">
              <button
                onClick={() => setShowCreate(false)}
                className="px-4 py-2 rounded-md text-sm font-medium text-gray-400 hover:text-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveCreate}
                disabled={createSaving}
                className="px-5 py-2 rounded-md text-sm font-semibold text-white bg-orange-500 hover:bg-orange-400 disabled:opacity-50 transition-colors"
              >
                {createSaving ? "Creating…" : "Create Bill"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showScanner && (
        <StatementScanner
          v3Projects={v3Projects}
          onClose={() => setShowScanner(false)}
        />
      )}
      {reviewBill && (
        <BillMOReviewPanel
          bill={reviewBill}
          projectId={reviewBill.project?._id || reviewBill.project}
          onClose={() => setReviewBill(null)}
          onConfirm={confirmBill} // reuse your existing confirmBill fn
        />
      )}

      {showBulkPaid && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm overflow-y-auto py-8 px-4">
          <div className="bg-gray-900 border border-gray-800 rounded-xl shadow-2xl w-full max-w-4xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
              <h3 className="text-base font-semibold text-white">
                Bulk Mark Paid by Invoice #
              </h3>
              <button
                onClick={() => setShowBulkPaid(false)}
                className="text-gray-500 hover:text-gray-300 text-lg leading-none"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className={labelCls}>Invoice Numbers</label>
                <textarea
                  rows={8}
                  className={inputCls + " resize-none font-mono"}
                  placeholder={
                    "Paste invoice numbers — one per line, or comma-separated\ne.g.\nIN1-0024392\nIN1-0024393\n28752"
                  }
                  value={bulkPaidText}
                  onChange={(e) => handleBulkPaidTextChange(e.target.value)}
                />
              </div>

              <div>
                <label className={labelCls}>
                  Date Paid (optional — defaults to today)
                </label>
                <input
                  type="date"
                  className={inputCls}
                  value={bulkPaidDate2}
                  onChange={(e) => handleBulkPaidDateChange(e.target.value)}
                />
              </div>

              <div>
                <label className={labelCls}>
                  Payment Fee % (optional — applied to every matched bill)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  className={inputCls}
                  placeholder="e.g. 2.9"
                  value={bulkPaidFeePct}
                  onChange={(e) => handleBulkPaidFeeChange(e.target.value)}
                />
              </div>

              {bulkPaidResult && !bulkPaidCommitted && (
                <div className="rounded-lg border border-amber-700/50 bg-amber-950/20 p-3 space-y-2 text-xs">
                  <p className="text-amber-300 font-medium">
                    Preview only — nothing has been changed yet.
                  </p>
                  <div className="flex gap-4">
                    <span className="text-emerald-400 font-semibold">
                      {bulkPaidResult.matchedCount} will be marked paid
                    </span>
                    <span className="text-amber-400">
                      {bulkPaidResult.unmatchedCount} not found
                    </span>
                  </div>

                  {bulkPaidResult.unmatched.length > 0 && (
                    <div>
                      <p className="text-gray-500 mb-1">Not found in system:</p>
                      <div className="flex flex-wrap gap-1">
                        {bulkPaidResult.unmatched.map((n) => (
                          <span
                            key={n}
                            className="px-1.5 py-0.5 rounded bg-red-900/40 border border-red-800/50 text-red-300 font-mono"
                          >
                            {n}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {bulkPaidResult.results.filter((r) => r.found).length > 0 && (
                    <div>
                      <p className="text-gray-500 mb-1">Matched:</p>
                      <div className="max-h-40 overflow-y-auto space-y-0.5">
                        {bulkPaidResult.results
                          .filter((r) => r.found)
                          .map((r) => (
                            <div
                              key={r.invoiceNumber}
                              className="flex justify-between text-gray-300"
                            >
                              <span className="font-mono">
                                {r.invoiceNumber}
                              </span>
                              <span className="text-gray-500 truncate max-w-[180px]">
                                {r.bill.billName}
                              </span>
                              <span className="text-gray-500">
                                ${fmt(r.bill.total)}
                              </span>
                              {r.bill.invoiceStatus === "paid" ? (
                                <span className="text-gray-600">
                                  already paid
                                </span>
                              ) : r.updated === false &&
                                r.bill.processingStatus ? (
                                <span className="text-amber-500">
                                  will update
                                </span>
                              ) : (
                                <span className="text-amber-500">
                                  will update
                                </span>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {bulkPaidCommitted && (
                <div className="rounded-lg border border-emerald-700/50 bg-emerald-950/20 p-3 space-y-2 text-xs">
                  <p className="text-emerald-300 font-medium">
                    ✓ {bulkPaidCommitted.updatedCount} bill(s) marked paid.
                  </p>
                  {bulkPaidCommitted.unmatched.length > 0 && (
                    <p className="text-amber-400">
                      {bulkPaidCommitted.unmatchedCount} invoice number(s) had
                      no match.
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-800">
              <button
                onClick={() => setShowBulkPaid(false)}
                className="px-4 py-2 rounded-md text-sm font-medium text-gray-400 hover:text-gray-200 transition-colors"
              >
                Close
              </button>

              {!bulkPaidCommitted && (
                <button
                  onClick={previewBulkMarkPaid}
                  disabled={bulkPaidPreviewing || !bulkPaidText.trim()}
                  className="px-5 py-2 rounded-md text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 transition-colors"
                >
                  {bulkPaidPreviewing ? "Looking up…" : "Find Matches"}
                </button>
              )}

              {bulkPaidResult && !bulkPaidCommitted && (
                <button
                  onClick={confirmBulkMarkPaid}
                  disabled={
                    bulkPaidConfirming || bulkPaidResult.matchedCount === 0
                  }
                  className="px-5 py-2 rounded-md text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 transition-colors"
                >
                  {bulkPaidConfirming
                    ? "Marking Paid…"
                    : `Confirm & Mark ${bulkPaidResult.matchedCount} Paid`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
